import React, { useEffect, useState } from "react";
import { injectIntl, InjectedIntlProps } from "react-intl";
import { useDispatch } from "react-redux";

import { AnalyzeAPI } from "@ai360/core";
import { RadioButtonGroup, RadioButton, Button, Loader, LoaderTypes } from "~/core";
import { PrintIcon } from "~/core/icons";
import { DialogTool } from "~/core/components/dialog-box/dialog-tool";
import { DialogBox, DialogBoxFooterType } from "~/core";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { selectors as cdSelectors } from "~/customer-data";
import { useAppSelector } from "~/store/hooks";

import MarkdownRenderer from "./components/markdownrenderer";
import NutrientChart from "./components/nutrientchart";
import { messages, errorMessages } from "./i18n-messages";
import { logFirebaseEvent } from "~/utils/firebase";

import "./analyze.css";
import "react-quill/dist/quill.snow.css";
import "../core/components/text-editor/text-editor.css";
import "../core/components/radio-button/radio-button.css";

function Analyze_(props: InjectedIntlProps) {
    const { formatMessage } = props.intl;
    const dispatch = useDispatch();

    // IMPORTANT //
    // Question ID, Message
    const analysisOptions = {
        1: formatMessage(messages.question1),
        2: formatMessage(messages.question2),
        3: formatMessage(messages.question3),
    };
    // Question ID, firebase msg
    const firebaseMsg: Record<number, string> = {
        1: "analyze_soil_deficiency",
        2: "analyze_soil_and_tissue_deficiency",
        3: "analyze_tissue_not_soil_deficiency",
    };

    const [selected, setSelected] = useState<string>(null);

    const [output, setOutput] = useState("");
    const [textOutput, setTextOutput] = useState("");
    const [graphOutput, setGraphOutput] = useState(undefined);
    const [legendOutput, setLegendOutput] = useState(undefined);
    const [pdfOutput, setPdfOutput] = useState(undefined);

    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [invalidFields, setInvalidFields] = useState([]);
    const [fieldsModal, setFieldsModal] = useState<[string | undefined, string | undefined]>([
        undefined,
        undefined,
    ]);

    const selectedFieldGuidSet = useAppSelector(cdSelectors.getSelectedFieldGuids);

    useEffect(() => {
        let fieldsText = "";
        if (invalidFields.length !== 0) {
            invalidFields.forEach((field) => {
                fieldsText += `${field}\n`;
            });
            fieldsText = "\n" + fieldsText.slice(0, -1);
            setFieldsModal([
                formatMessage(errorMessages.invalidFieldsWarning, { fieldsText }),
                formatMessage(errorMessages.invalidFieldsModalTitle),
            ]);
        }
    }, [invalidFields]);

    useEffect(() => {
        const lines = output
            .split(/\n|\}\{/)
            .filter((line) => line.trim() !== "")
            .map((line, i, arr) => {
                const trimmed = line.trim();
                if (
                    i < arr.length - 1 &&
                    !trimmed.endsWith("}") &&
                    i > 0 &&
                    !trimmed.startsWith("{")
                )
                    return "{" + trimmed + "}";
                if (i < arr.length - 1 && !trimmed.endsWith("}")) return trimmed + "}";
                if (i > 0 && !trimmed.startsWith("{")) return "{" + trimmed;
                return trimmed;
            });

        let textAdd = "";
        let graphOut = undefined;
        let pdfOut = undefined;

        for (const line of lines) {
            try {
                const parsed = JSON.parse(line);

                switch (parsed.type) {
                    case "content_block_delta":
                        if (parsed.delta?.type === "text_delta") {
                            textAdd += parsed.delta.text;
                        }
                        break;
                    case "GraphModel_Soil":
                        graphOut = parsed.GraphModel_Soil;
                        break;
                    case "levelNames":
                        setLegendOutput(parsed.levelNames.split("\t"));
                        break;
                    case "pdf":
                        pdfOut = parsed.pdf;
                        break;
                }
            } catch {
                // Partial JSON line, skip and wait for next chunk
            }
        }
        setTextOutput(textAdd);
        setGraphOutput(graphOut);
        setPdfOutput(pdfOut);
        if (textAdd != "" || graphOut != undefined) {
            setIsLoading(false);
        }
    }, [output]);

    const handleSubmit = async () => {
        if (isStreaming) {
            return;
        }
        if (selected === null || selectedFieldGuidSet.size === 0) {
            setFieldsModal([
                formatMessage(errorMessages.invalidSelectionError),
                formatMessage(errorMessages.invalidSelectionModalTitle),
            ]);
            return;
        }

        logFirebaseEvent(firebaseMsg[selected]);

        setOutput("");
        setIsStreaming(true);
        setIsLoading(true);

        try {
            await AnalyzeAPI.getAnalyzeInfo(
                Array.from(selectedFieldGuidSet),
                parseInt(selected),
                (chunk) => setOutput((prev) => prev + chunk),
                () => setIsStreaming(false),
                (fields) => setInvalidFields(fields)
            );
        } catch (error) {
            const apiResult = error["apiResultObj"];
            const message = apiResult?.["innerMessage"] ?? apiResult?.["message"];
            const title = apiResult?.["innerMessage"] != null ? apiResult?.["message"] : null;
            if (message) {
                setFieldsModal([message, title ?? formatMessage(errorMessages.genericModalTitle)]);
            } else {
                dispatch(
                    notificationActions.pushToasterMessage(
                        formatMessage(errorMessages.generic),
                        MSGTYPE.ERROR
                    )
                );
            }
        }
        setIsStreaming(false);
        setIsLoading(false);
    };

    const onPrint = () => {
        if (pdfOutput === undefined) {
            return "";
        }

        const date = new Date()
            .toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            })
            .replace(/\//g, "-");

        const randomNum = String(Math.floor(Math.random() * 1000000)).padStart(6, "0");

        const fileName = `AnalysisResult_${date}_${randomNum}`;

        const linkSource = `data:application/pdf;base64,${pdfOutput}`;
        const downloadLink = document.createElement("a");

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
    };

    const modal = (
        <DialogBox
            isOpen={fieldsModal[0] !== undefined}
            onClose={() => {
                setFieldsModal([undefined, undefined]);
            }}
            footerType={DialogBoxFooterType.NONE}
            title={fieldsModal[1]}
        >
            {fieldsModal[0]?.split("\n").map((line, i, arr) => (
                <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                </span>
            ))}
        </DialogBox>
    );

    const radioButtons = (
        <div className="analysisOptions">
            <RadioButtonGroup
                afterOnChange={(value) => {
                    if (typeof value !== "boolean") {
                        setSelected(value);
                    }
                }}
                selectedValue={selected}
            >
                {Object.entries(analysisOptions).map(([value, label]) => (
                    <RadioButton key={value} label={label} value={value} />
                ))}
            </RadioButtonGroup>
        </div>
    );

    const selectAnalysisTypeSection = (
        <section>
            <div className="topRowConainter">
                <h1 className="sectionTitle">{formatMessage(messages.select)}</h1>
                <Button type="analyze" forceSubmit onClick={handleSubmit} />
            </div>
            {radioButtons}
        </section>
    );

    const resultSection = (
        <div className="resultSection">
            <div className="topRowConainter">
                <h1 className="sectionTitle">{formatMessage(messages.result)}</h1>
                <DialogTool onClick={() => onPrint()}>
                    <PrintIcon className="printIcon" />
                </DialogTool>
            </div>
            <div className="ql-container ql-snow" id="analysis-result">
                <div className="ql-editor">
                    <div className="analysis-result">
                        <MarkdownRenderer markdown={textOutput} />
                        {graphOutput && graphOutput.length > 0 && (
                            <NutrientChart data={graphOutput} legendData={legendOutput} />
                        )}
                        <p>{formatMessage(messages.aiWarning)}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="add-edit-panel" style={{ overflow: "hidden", margin: "15px 20px" }}>
            <div className="analyze">
                {selectAnalysisTypeSection}
                {isLoading ? (
                    <Loader
                        className="layer-loader"
                        type={LoaderTypes.LINE_SCALE_PULSE_OUT_RAPID}
                    />
                ) : null}
                {output === "" ? <></> : resultSection}
            </div>
            {modal}
        </div>
    );
}

export const Analyze = injectIntl(Analyze_);
